import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  Typography,
  Descriptions,
  Button,
  Empty,
  Card,
  Col,
  Row,
  Modal,
  Popover,
  Space,
  Switch,
  Select,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import { timeIntervalOption, timeRange } from "../../helpers/metrics.option";
import { ENDPOINT_QUERY } from "../../helpers/graphql/endpoint";
import { CreateEndpoint } from "../modal/CreateEndpoint";
import { EndpointMetrics } from "./EndpointMetrics";
import { EndpointMethodMetrics } from "./EndpointMethodMetrics";
import { EndpointOriginMetrics } from "./EndpointOriginMetrics";
import { EndpointRegionMetrics } from "./EndpointRegionMetrics";

interface Props {
  project: { id: string };
  chain: string;
  network: string;
}

export function Endpoint({ project, network, chain }: Props) {
  const [visible, setVisible] = useState(false);
  const [isCreateArchive, setIsCreateArchive] = useState(false);
  const [isCreateWebsocket, setIsCreateWebsocket] = useState(false);

  const [archive, setArchive] = useState(false);
  const [interval, setInterval] = useState("15m");
  const [showLast, setShowLast] = useState("_1d");

  const variables = useMemo(() => {
    return {
      projectId: project.id,
      chain,
      network,
    };
  }, [project, chain, network]);

  const { loading, data } = useQuery(ENDPOINT_QUERY, {
    variables,
    errorPolicy: "all",
  });

  const showCreateArchiveModal = () => {
    setVisible(true);
    setIsCreateArchive(true);
    setIsCreateWebsocket(false);
  };

  const showCreateWebsocketModal = () => {
    setVisible(true);
    setIsCreateArchive(false);
    setIsCreateWebsocket(true);
  };

  const showCreateArchiveWebsocketModal = () => {
    setVisible(true);
    setIsCreateArchive(true);
    setIsCreateWebsocket(true);
  };

  const hideModal = () => {
    setVisible(false);
  };

  const endpoint = data?.endpoint;
  const endpointArchive = data?.endpointArchive;
  const endpointWebsocket = data?.endpointWebsocket;
  const endpointArchiveWebsocket = data?.endpointArchiveWebsocket;

  return (
    <>
      {loading ? (
        <Card loading={loading} bordered={false} className="card-loading" />
      ) : endpoint ? (
        <Row gutter={[10, 10]}>
          <Col md={24}>
            <Descriptions
              layout="vertical"
              column={{ xxl: 2, lg: 1, md: 1, sm: 1, xs: 1 }}
              colon={false}
            >
              <Descriptions.Item
                label={
                  <div>
                    Pruned node RPC
                    <Popover
                      placement="right"
                      content={
                        <Space direction="vertical">Recent data only</Space>
                      }
                      trigger="hover"
                    >
                      <InfoCircleOutlined style={{ marginLeft: 5 }} />
                    </Popover>
                  </div>
                }
              >
                <Typography.Text copyable>{endpoint?.url}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div>
                    Archive node RPC
                    <Popover
                      placement="right"
                      content={
                        <Space direction="vertical">Full historical data</Space>
                      }
                      trigger="hover"
                    >
                      <InfoCircleOutlined style={{ marginLeft: 5 }} />
                    </Popover>
                  </div>
                }
              >
                {endpointArchive?.url ? (
                  <Typography.Text copyable>{endpointArchive.url}</Typography.Text>
                ) : (
                  <Button type="primary" onClick={showCreateArchiveModal}>
                    Request endpoint
                  </Button>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col md={24}>
            <Descriptions
              layout="vertical"
              column={{ xxl: 2, lg: 1, md: 1, sm: 1, xs: 1 }}
              colon={false}
            >
              <Descriptions.Item
                label={
                  <div>
                    Pruned node websocket
                    <Popover
                      placement="right"
                      content={
                        <Space direction="vertical">Recent data only</Space>
                      }
                      trigger="hover"
                    >
                      <InfoCircleOutlined style={{ marginLeft: 5 }} />
                    </Popover>
                  </div>
                }
              >
                {endpointWebsocket?.url ? (
                  <Typography.Text copyable>{endpointWebsocket.url}</Typography.Text>
                ) : (
                  <Button type="primary" onClick={showCreateWebsocketModal}>
                    Request endpoint
                  </Button>
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <div>
                    Archive node websocket
                    <Popover
                      placement="right"
                      content={
                        <Space direction="vertical">Full historical data</Space>
                      }
                      trigger="hover"
                    >
                      <InfoCircleOutlined style={{ marginLeft: 5 }} />
                    </Popover>
                  </div>
                }
              >
                {endpointArchiveWebsocket?.url ? (
                  <Typography.Text copyable>{endpointArchiveWebsocket.url}</Typography.Text>
                ) : (
                  <Button type="primary" onClick={showCreateArchiveWebsocketModal}>
                    Request endpoint
                  </Button>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={24} style={{ textAlign: "end", marginTop: 16 }}>
            <Descriptions
              layout="vertical"
              colon={false}
              column={{ md: 3 }}
              labelStyle={{ alignItems: "center" }}
              style={{ width: "50wv", minWidth: 270 }}
            >
              <Descriptions.Item label="Interval" span={1}>
                <Select
                  size="small"
                  defaultValue={interval}
                  onChange={(value) => setInterval(value)}
                  options={timeIntervalOption}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Show last" span={1}>
                <Select
                  size="small"
                  defaultValue={showLast}
                  onChange={(value) => setShowLast(value)}
                  options={timeRange}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Node type" span={1}>
                <Switch
                  defaultChecked
                  checkedChildren="Full node"
                  unCheckedChildren="Archive node"
                  onChange={() => setArchive(!archive)}
                />
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col sm={24} md={24} lg={12} xl={12}>
            <EndpointMetrics
              project={project}
              network={network}
              chain={chain}
              archive={archive}
              interval={interval}
              showLast={showLast}
              height={190}
            />
          </Col>
          <Col sm={24} md={24} lg={12} xl={12}>
            <EndpointOriginMetrics
              project={project}
              network={network}
              chain={chain}
              archive={archive}
              interval={interval}
              showLast={showLast}
              height={190}
            />
          </Col>
          <Col sm={24} md={24} lg={12} xl={12}>
            <EndpointMethodMetrics
              project={project}
              network={network}
              chain={chain}
              archive={archive}
              interval={interval}
              showLast={showLast}
              height={190}
            />
          </Col>
          <Col sm={24} md={24} lg={12} xl={12}>
            <EndpointRegionMetrics
              project={project}
              network={network}
              chain={chain}
              archive={archive}
              interval={interval}
              showLast={showLast}
              height={190}
            />
          </Col>
        </Row>
      ) : (
        <Empty
          className="ant-empty-card-hero"
          description={
            <span>
              Request your {chain} {network} endpoint
            </span>
          }
        >
          <CreateEndpoint project={project} chain={chain} network={network} archive={false} websocket={false} />
        </Empty>
      )}

      <Modal
        title={`Request ${isCreateArchive ? 'archive' : 'pruned'} node ${isCreateWebsocket ? 'websocket' : 'RPC'} endpoint`}
        visible={visible}
        onCancel={hideModal}
        footer={[
          <Button key="cancel" onClick={hideModal}>
            Cancel
          </Button>,

          <CreateEndpoint
            project={project}
            chain={chain}
            network={network}
            archive={isCreateArchive}
            websocket={isCreateWebsocket}
            callback={hideModal}
          />,
        ]}
      >
      <>
        {isCreateArchive ? (
          <p>
            "Archive node" provides access to historical data on the network. If your application
            does not need to access historical chain state, we recommand you to use "Pruned node".
          </p>
        ) : (
          <p>
            "Pruned node" provides access to recent data on the network. It should be enough
            for most of the use cases.
          </p>
        )}
      </>
      </Modal>
    </>
  );
}
